import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  addDoc,
  getDocs,
  doc,
  query,
  where,
  setDoc,
  getDoc,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { db } from "../firebaseConfig";
import Swal from "sweetalert2";
import { DragDropContext } from "react-beautiful-dnd";
import TopBar from "./TopBar";
import GeneralInfo from "./GeneralInfo";
import ProductSelector from "./ProductSelector";
import RightPanel from "./RightPanel";
import FilesSection from "./FilesSection";
import ModalConfig from "./ModalConfig";

const CalculadorForm = ({
  closeModal,
  onSaveSuccess,
  initialData,
  setConfig,
}) => {
  const [nombre, setNombre] = useState("");
  const [numero, setNumero] = useState("");
  const [cliente, setCliente] = useState("");
  const [fechaEmision, setFechaEmision] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [fechaVencimiento, setFechaVencimiento] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [moneda, setMoneda] = useState("MXN");
  const [impuesto, setImpuesto] = useState("16%");
  const [clientesList, setClientesList] = useState([]);
  const [modalConfigIsOpen, setModalConfigIsOpen] = useState(false);
  const [modoNumero, setModoNumero] = useState("auto");
  const [prefijo, setPrefijo] = useState("IC-");
  const [siguienteNumero, setSiguienteNumero] = useState("");
  const [editableNumero, setEditableNumero] = useState(false);
  const [productos, setProductos] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [quantities, setQuantities] = useState({});
  const [hoveredProductId, setHoveredProductId] = useState(null);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [activeTab, setActiveTab] = useState("todos");
  const [addedGroups, setAddedGroups] = useState([]);
  const [addedProducts, setAddedProducts] = useState([]);
  const [equipoSearchTerm, setEquipoSearchTerm] = useState("");
  const [files, setFiles] = useState([]);
  const [checkedItems, setCheckedItems] = useState([]);
  const [productSearchTerm, setProductSearchTerm] = useState("");
  const [editMode, setEditMode] = useState(initialData ? false : true);
  const [unassignedTitle, setUnassignedTitle] = useState("Productos");
  const [isLoading, setIsLoading] = useState(true);

  const fileInputRef = useRef(null);

  // Simulación de carga
  useEffect(() => {
    const timeout = setTimeout(() => setIsLoading(false), 1500);
    return () => clearTimeout(timeout);
  }, []);

  // Suscripciones a Firebase
  useEffect(() => {
    const unsubscribes = [];
    unsubscribes.push(
      onSnapshot(collection(db, "clientes"), (snapshot) => {
        const data = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClientesList(
          data.filter(
            (cliente, index, self) =>
              index === self.findIndex((c) => c.empresa === cliente.empresa)
          )
        );
      })
    );
    unsubscribes.push(
      onSnapshot(collection(db, "grupos"), (snapshot) => {
        setGrupos(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
      })
    );
    unsubscribes.push(
      onSnapshot(collection(db, "productos"), (snapshot) => {
        setProductos(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      })
    );
    unsubscribes.push(
      onSnapshot(collection(db, "categorias"), (snapshot) => {
        setCategorias(
          snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }))
        );
      })
    );
    return () => unsubscribes.forEach((unsub) => unsub());
  }, []);

  // Rellenar datos en modo edición
  useEffect(() => {
    if (initialData && initialData.id) {
      setNombre(initialData.nombre || "");
      setNumero(initialData.numero || "");
      setCliente(initialData.cliente || "");
      setFechaEmision(
        initialData.fechaEmision
          ? initialData.fechaEmision
          : new Date().toISOString().split("T")[0]
      );
      setFechaVencimiento(initialData.fechaVencimiento || "");
      setDescripcion(initialData.descripcion || "");
      setMoneda(initialData.moneda || "MXN");
      setImpuesto(initialData.impuesto || "16%");
      setAddedProducts(initialData.addedProducts || []);
      setAddedGroups(initialData.addedGroups || []);
      setUnassignedTitle(initialData.unassignedTitle || "Productos");
    } else {
      setNombre("");
      setNumero("");
      setCliente("");
      setFechaEmision(new Date().toISOString().split("T")[0]);
      setFechaVencimiento("");
      setDescripcion("");
      setMoneda("MXN");
      setImpuesto("16%");
      setAddedProducts([]);
      setAddedGroups([]);
      setUnassignedTitle("Productos");
    }
  }, [initialData]);

  // Leer configuración de numeración
  useEffect(() => {
    async function leerConfig() {
      const docRef = doc(db, "config", "numeracion");
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        setPrefijo(data.prefijo || "IC-");
        setSiguienteNumero(data.siguienteNumero?.toString() || "4511");
        setModoNumero(data.modoNumero || "auto");
        if (!initialData?.id && data.modoNumero === "auto") {
          const numFormateado = `${data.prefijo}${String(
            data.siguienteNumero
          ).padStart(4, "0")}`;
          setNumero(numFormateado);
          setEditableNumero(false);
        }
      } else {
        await setDoc(docRef, {
          prefijo: "IC-",
          siguienteNumero: 4511,
          modoNumero: "auto",
        });
        setPrefijo("IC-");
        setSiguienteNumero("4511");
        setModoNumero("auto");
        if (!initialData?.id) {
          setNumero("IC-4511");
          setEditableNumero(false);
        }
      }
    }
    leerConfig();
  }, [initialData]);

  // Funciones para file upload
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    else if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const simulateUpload = (fileId) => {
    const intervalId = setInterval(() => {
      setFiles((prevFiles) =>
        prevFiles.map((f) => {
          if (f.id === fileId && f.status === "uploading") {
            let newProgress = f.progress + 10;
            if (newProgress >= 100) {
              newProgress = 100;
              f.status = "success";
              clearInterval(intervalId);
            }
            return { ...f, progress: newProgress };
          }
          return f;
        })
      );
    }, 400);
  };

  const handleFileSelect = (e) => {
    const selectedFiles = e.target.files;
    if (!selectedFiles) return;
    const filesArray = Array.from(selectedFiles).map((file) => ({
      id: Date.now() + Math.random(),
      nombre: file.name,
      size: formatFileSize(file.size),
      progress: 0,
      status: "uploading",
      date: new Date().toLocaleDateString(),
    }));
    setFiles((prev) => [...prev, ...filesArray]);
    filesArray.forEach((f) => simulateUpload(f.id));
  };

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleDeleteFile = (fileId) => {
    setFiles((prev) => prev.filter((f) => f.id !== fileId));
  };

  const handleProductQuantityChange = (groupName, productId, newQuantity) => {
    const quantity = parseInt(newQuantity, 10) || 0;
    setAddedGroups((prev) =>
      prev.map((group) => {
        if (group.groupName === groupName) {
          const updatedProducts = group.products.map((prod) =>
            prod.id === productId
              ? { ...prod, quantity, subtotal: prod.costo * quantity }
              : prod
          );
          return { ...group, products: updatedProducts };
        }
        return group;
      })
    );
  };

  const handleUnassignedProductQuantityChange = (
    productId,
    index,
    newQuantity
  ) => {
    const quantity = parseInt(newQuantity, 10) || 0;
    setAddedProducts((prev) =>
      prev.map((prod, idx) =>
        prod.id === productId && idx === index
          ? { ...prod, quantity, subtotal: prod.costo * quantity }
          : prod
      )
    );
  };

  const handleCostChangeInGroup = (groupName, prodId, valueWithCommas) => {
    const numericValue = parseFloat(valueWithCommas.replace(/,/g, "")) || 0;
    setAddedGroups((prev) =>
      prev.map((group) => {
        if (group.groupName === groupName) {
          const updatedProducts = group.products.map((p) => {
            if (p.id === prodId) {
              return {
                ...p,
                costo: numericValue,
                subtotal: numericValue * p.quantity,
              };
            }
            return p;
          });
          return { ...group, products: updatedProducts };
        }
        return group;
      })
    );
  };

  const handleCostChangeInUnassigned = (prodId, index, valueWithCommas) => {
    const numericValue = parseFloat(valueWithCommas.replace(/,/g, "")) || 0;
    setAddedProducts((prev) =>
      prev.map((p, i) => {
        if (p.id === prodId && i === index) {
          return {
            ...p,
            costo: numericValue,
            subtotal: numericValue * p.quantity,
          };
        }
        return p;
      })
    );
  };

  const handleGroupTitleChange = (groupIndex, newTitle) => {
    setAddedGroups((prev) => {
      const newGroups = [...prev];
      newGroups[groupIndex] = {
        ...newGroups[groupIndex],
        groupName: newTitle,
      };
      return newGroups;
    });
  };

  const handleCheckAllUnassigned = (productList) => {
    const rowIds = productList.map(
      (prod, idx) => `unassigned-${prod.id}-${idx}`
    );
    const allSelected = rowIds.every((rId) => checkedItems.includes(rId));
    if (allSelected) {
      setCheckedItems((prev) => prev.filter((id) => !rowIds.includes(id)));
    } else {
      setCheckedItems((prev) => [...new Set([...prev, ...rowIds])]);
    }
  };

  const handleDeleteSelectedInUnassigned = () => {
    setAddedProducts((prev) =>
      prev.filter((prod, idx) => {
        const rowId = `unassigned-${prod.id}-${idx}`;
        return !checkedItems.includes(rowId);
      })
    );
    setCheckedItems((prev) =>
      prev.filter((rowId) => !rowId.startsWith("unassigned-"))
    );
  };

  const handleCheckItem = (rowId) => {
    setCheckedItems((prev) =>
      prev.includes(rowId)
        ? prev.filter((id) => id !== rowId)
        : [...prev, rowId]
    );
  };

  const handleCheckAllGroup = (grupo) => {
    const rowIds = grupo.products.map(
      (prod, idx) => `group-${grupo.groupName}-${prod.id}-${idx}`
    );
    const todosEstanSeleccionados = rowIds.every((rId) =>
      checkedItems.includes(rId)
    );
    if (todosEstanSeleccionados) {
      setCheckedItems((prev) => prev.filter((id) => !rowIds.includes(id)));
    } else {
      setCheckedItems((prev) => [...new Set([...prev, ...rowIds])]);
    }
  };

  const handleDeleteSelectedInGroup = (groupName) => {
    setAddedGroups((prev) =>
      prev
        .map((g) => {
          if (g.groupName === groupName) {
            const nuevos = g.products.filter((prod, idx) => {
              const rowId = `group-${groupName}-${prod.id}-${idx}`;
              return !checkedItems.includes(rowId);
            });
            return { ...g, products: nuevos };
          }
          return g;
        })
        .filter((g) => g.products.length > 0)
    );
    setCheckedItems((prev) =>
      prev.filter((rowId) => !rowId.startsWith(`group-${groupName}-`))
    );
  };

  const reorder = (list, startIndex, endIndex) => {
    const result = Array.from(list);
    const [removed] = result.splice(startIndex, 1);
    result.splice(endIndex, 0, removed);
    return result;
  };

  const normalizeName = (name) => name.trim().toLowerCase();

  const generateUniqueGroupName = (baseName) => {
    const normalizedBase = normalizeName(baseName);
    const existing = addedGroups.filter((g) =>
      normalizeName(g.groupName).startsWith(normalizedBase)
    );
    if (existing.length === 0) return baseName;
    return `${baseName} (${existing.length + 1})`;
  };

  const handleDrop = (e) => {
    e.preventDefault();

    // 1) Ignorar drops que no sean "application/json" (drag interno de RBDnD).
    if (!e.dataTransfer.types.includes("application/json")) {
      return;
    }

    // 2) Parseamos el payload
    const data = e.dataTransfer.getData("application/json");
    if (!data) return;

    let payload;
    try {
      payload = JSON.parse(data);
    } catch (err) {
      console.error("Error al parsear los datos del drop:", err);
      return;
    }

    const { items, itemType } = payload;

    // === FUNCIONES AUXILIARES ===
    const generateUniqueGroupName = (baseName) => {
      const normalizeName = (name) => name.trim().toLowerCase();
      const normalizedBase = normalizeName(baseName);
      const existing = addedGroups.filter((g) =>
        normalizeName(g.groupName).startsWith(normalizedBase)
      );
      if (existing.length === 0) return baseName;
      return `${baseName} (${existing.length + 1})`;
    };

    // Detecta si cayó sobre la zona "Nuevo Grupo"
    const newTableEl = document.getElementById("newTableDroppable");
    const droppedOnNewTable = newTableEl && newTableEl.contains(e.target);

    // Detectar si cayó sobre algún grupo existente:
    // Recorremos todos los div con id que empiece con "drop-group-"
    let droppedGroupName = null;
    const allGroupDivs = document.querySelectorAll('[id^="drop-group-"]');
    allGroupDivs.forEach((div) => {
      if (div.contains(e.target)) {
        // Este div contiene el drop
        droppedGroupName = div.id.replace("drop-group-", "");
      }
    });

    // Si se soltó sobre la zona "Nuevo Grupo"
    if (droppedOnNewTable) {
      if (itemType === "group") {
        items.forEach((groupItem) => {
          const uniqueName = generateUniqueGroupName(groupItem.nombre);
          const groupProducts = productos.filter(
            (p) => p.grupo === groupItem.nombre
          );
          const mappedProds = groupProducts.map((p) => {
            const q = quantities[p.id] || 1;
            const cost = parseFloat(p.costo) || 0;
            return { ...p, quantity: q, subtotal: cost * q };
          });
          setAddedGroups((prev) => [
            ...prev,
            { groupName: uniqueName, products: mappedProds },
          ]);
        });
      } else if (itemType === "product") {
        // Si se sueltan productos sobre "Nuevo Grupo", se agregan a "Productos sin grupo"
        const mappedItems = items.map((prodItem) => {
          const q = quantities[prodItem.id] || 1;
          const cost = parseFloat(prodItem.costo) || 0;
          return { ...prodItem, quantity: q, subtotal: cost * q };
        });
        setAddedProducts((prev) => {
          const newArr = [...prev];
          mappedItems.forEach((prod) => {
            if (!newArr.some((p) => p.id === prod.id)) {
              newArr.push(prod);
            }
          });
          return newArr;
        });
      } else {
        // Fallback: procesa cada ítem (si fuese necesario)
        items.forEach((item) => {
          if (grupos.some((g) => g.id === item.id)) {
            const uniqueName = generateUniqueGroupName(item.nombre);
            const groupProducts = productos.filter(
              (p) => p.grupo === item.nombre
            );
            const mappedProds = groupProducts.map((p) => {
              const q = quantities[p.id] || 1;
              const cost = parseFloat(p.costo) || 0;
              return { ...p, quantity: q, subtotal: cost * q };
            });
            setAddedGroups((prev) => [
              ...prev,
              { groupName: uniqueName, products: mappedProds },
            ]);
          }
        });
      }
      return; // Salir para no continuar con la lógica por defecto.
    }

    // Si cayó sobre un grupo existente...
    if (droppedGroupName) {
      // 4a) Verificamos si el grupo existe en "addedGroups"
      const groupIndex = addedGroups.findIndex(
        (g) => g.groupName === droppedGroupName
      );
      if (groupIndex === -1) {
        console.warn("No se encontró el grupo " + droppedGroupName);
        return;
      }

      // 4b) Agregar los items a ese grupo (fusionando cantidades si ya existe el producto)
      setAddedGroups((prev) => {
        const newGroups = [...prev];
        const targetGroup = { ...newGroups[groupIndex] };
        const groupProducts = [...targetGroup.products];

        items.forEach((prodItem) => {
          const q = quantities[prodItem.id] || 1;
          const cost = parseFloat(prodItem.costo) || 0;
          const newItem = { ...prodItem, quantity: q, subtotal: cost * q };

          const existingIndex = groupProducts.findIndex(
            (p) => p.id === newItem.id
          );
          if (existingIndex >= 0) {
            groupProducts[existingIndex].quantity += newItem.quantity;
            groupProducts[existingIndex].subtotal += newItem.subtotal;
          } else {
            groupProducts.push(newItem);
          }
        });

        targetGroup.products = groupProducts;
        newGroups[groupIndex] = targetGroup;
        return newGroups;
      });
      return;
    }

    // Si no cayó en "Nuevo Grupo" ni en un grupo existente...
    // Se procesa el item según su tipo:
    if (itemType === "group") {
      // Procesar grupos: se agregan a grupos existentes (si ya existen se fusiona)
      items.forEach((groupItem) => {
        const groupProducts = productos.filter(
          (p) => p.grupo === groupItem.nombre
        );
        const mappedProds = groupProducts.map((p) => {
          const q = quantities[p.id] || 1;
          const cost = parseFloat(p.costo) || 0;
          return { ...p, quantity: q, subtotal: cost * q };
        });
        const newGroup = {
          groupName: groupItem.nombre,
          products: mappedProds,
        };

        setAddedGroups((prev) => {
          const idx = prev.findIndex((g) => g.groupName === newGroup.groupName);
          if (idx >= 0) {
            const updatedGroup = { ...prev[idx] };
            mappedProds.forEach((prod) => {
              if (!updatedGroup.products.some((p) => p.id === prod.id)) {
                updatedGroup.products.push(prod);
              }
            });
            const newArr = [...prev];
            newArr[idx] = updatedGroup;
            return newArr;
          } else {
            return [...prev, newGroup];
          }
        });
      });
    } else {
      // Para productos, se agrega el producto a "Productos sin grupo"
      // (Se ha eliminado el bloque "else if (itemType === 'product')" para evitar crear grupos)
      // En este caso, el fallback se encarga de procesarlos:
      items.forEach((item) => {
        if (grupos.some((g) => g.id === item.id)) {
          const groupProducts = productos.filter(
            (p) => p.grupo === item.nombre
          );
          const mappedProds = groupProducts.map((p) => {
            const q = quantities[p.id] || 1;
            const cost = parseFloat(p.costo) || 0;
            return { ...p, quantity: q, subtotal: cost * q };
          });
          const newGroup = { groupName: item.nombre, products: mappedProds };

          setAddedGroups((prev) => {
            const idx = prev.findIndex(
              (g) => g.groupName === newGroup.groupName
            );
            if (idx >= 0) {
              const updatedGroup = { ...prev[idx] };
              mappedProds.forEach((prod) => {
                if (!updatedGroup.products.some((p) => p.id === prod.id)) {
                  updatedGroup.products.push(prod);
                }
              });
              const newArr = [...prev];
              newArr[idx] = updatedGroup;
              return newArr;
            } else {
              return [...prev, newGroup];
            }
          });
        } else {
          // Producto individual
          const q = quantities[item.id] || 1;
          const cost = parseFloat(item.costo) || 0;
          const newItem = { ...item, quantity: q, subtotal: cost * q };
          setAddedProducts((prev) => {
            const newArr = [...prev];
            if (!newArr.some((p) => p.id === newItem.id)) {
              newArr.push(newItem);
            }
            return newArr;
          });
        }
      });
    }
  };

  const handleDragEnd = (result) => {
    const { source, destination, reason } = result;
    if (!destination || reason === "CANCEL") return;
    const normalizeName = (name) => name.trim().toLowerCase();
    const isGroupUnique = (rawName) => {
      const normalized = normalizeName(rawName);
      return (
        addedGroups.filter((g) => normalizeName(g.groupName) === normalized)
          .length === 1
      );
    };

    if (source.droppableId === destination.droppableId) {
      if (source.droppableId === "unassignedDroppable") {
        const newProducts = reorder(
          addedProducts,
          source.index,
          destination.index
        );
        setAddedProducts(newProducts);
      } else if (source.droppableId.startsWith("group-")) {
        const groupName = source.droppableId.replace("group-", "");
        const groupIndex = addedGroups.findIndex(
          (g) => g.groupName === groupName
        );
        if (groupIndex !== -1) {
          const newProducts = reorder(
            addedGroups[groupIndex].products,
            source.index,
            destination.index
          );
          const newGroups = [...addedGroups];
          newGroups[groupIndex] = {
            ...newGroups[groupIndex],
            products: newProducts,
          };
          setAddedGroups(newGroups);
        }
      }
      setAddedGroups((prev) => prev.filter((g) => g.products.length > 0));
      return;
    }

    if (
      source.droppableId === "unassignedDroppable" &&
      destination.droppableId.startsWith("group-")
    ) {
      const rawGroupName = destination.droppableId.replace("group-", "");
      if (!isGroupUnique(rawGroupName)) {
        Swal.fire({
          icon: "error",
          title: "Nombre de grupo duplicado",
          text: "No se puede mover el producto a un grupo con nombre duplicado.",
        });
        return;
      }
      const sourceProducts = [...addedProducts];
      const [movedItem] = sourceProducts.splice(source.index, 1);
      setAddedProducts(sourceProducts);
      const groupIndex = addedGroups.findIndex(
        (g) => g.groupName === rawGroupName
      );
      if (groupIndex !== -1) {
        const newGroupProducts = [...addedGroups[groupIndex].products];
        const existingProductIndex = newGroupProducts.findIndex(
          (p) => p.id === movedItem.id
        );
        if (existingProductIndex !== -1) {
          newGroupProducts[existingProductIndex].quantity += movedItem.quantity;
          newGroupProducts[existingProductIndex].subtotal += movedItem.subtotal;
        } else {
          newGroupProducts.splice(destination.index, 0, movedItem);
        }
        const newGroups = [...addedGroups];
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          products: newGroupProducts,
        };
        setAddedGroups(newGroups);
      } else {
        setAddedGroups([
          ...addedGroups,
          { groupName: rawGroupName, products: [movedItem] },
        ]);
      }
      setAddedGroups((prev) => prev.filter((g) => g.products.length > 0));
      return;
    }

    if (
      source.droppableId.startsWith("group-") &&
      destination.droppableId.startsWith("group-") &&
      source.droppableId !== destination.droppableId
    ) {
      const sourceGroupName = source.droppableId.replace("group-", "");
      const destGroupName = destination.droppableId.replace("group-", "");
      if (!isGroupUnique(destGroupName)) {
        Swal.fire({
          icon: "error",
          title: "Nombre de grupo duplicado",
          text: "No se puede mover el producto a un grupo con nombre duplicado.",
        });
        return;
      }
      const sourceIndexG = addedGroups.findIndex(
        (g) => g.groupName === sourceGroupName
      );
      const destIndexG = addedGroups.findIndex(
        (g) => g.groupName === destGroupName
      );
      if (sourceIndexG === -1 || destIndexG === -1) return;
      const sourceGroupProducts = [...addedGroups[sourceIndexG].products];
      if (source.index < 0 || source.index >= sourceGroupProducts.length)
        return;
      const [movedItem] = sourceGroupProducts.splice(source.index, 1);
      if (!movedItem) return;
      const destGroupProducts = [...addedGroups[destIndexG].products];
      const existingProductIndex = destGroupProducts.findIndex(
        (p) => p.id === movedItem.id
      );
      if (existingProductIndex !== -1) {
        destGroupProducts[existingProductIndex].quantity += movedItem.quantity;
        destGroupProducts[existingProductIndex].subtotal += movedItem.subtotal;
      } else {
        destGroupProducts.splice(destination.index, 0, movedItem);
      }
      const newGroups = [...addedGroups];
      newGroups[sourceIndexG] = {
        ...newGroups[sourceIndexG],
        products: sourceGroupProducts,
      };
      newGroups[destIndexG] = {
        ...newGroups[destIndexG],
        products: destGroupProducts,
      };
      setAddedGroups(newGroups);
      setAddedGroups((prev) => prev.filter((g) => g.products.length > 0));
      return;
    }

    if (
      source.droppableId.startsWith("group-") &&
      destination.droppableId === "unassignedDroppable"
    ) {
      const sourceGroupName = source.droppableId.replace("group-", "");
      const groupIndex = addedGroups.findIndex(
        (g) => g.groupName === sourceGroupName
      );
      if (groupIndex !== -1) {
        const sourceGroupProducts = [...addedGroups[groupIndex].products];
        const [movedItem] = sourceGroupProducts.splice(source.index, 1);
        if (!movedItem) return;
        const existingIndex = addedProducts.findIndex(
          (p) => p.id === movedItem.id
        );
        if (existingIndex !== -1) {
          addedProducts[existingIndex].quantity += movedItem.quantity;
          addedProducts[existingIndex].subtotal += movedItem.subtotal;
        } else {
          addedProducts.splice(destination.index, 0, movedItem);
        }
        const newGroups = [...addedGroups];
        newGroups[groupIndex] = {
          ...newGroups[groupIndex],
          products: sourceGroupProducts,
        };
        setAddedGroups(newGroups);
        setAddedProducts([...addedProducts]);
      }
      setAddedGroups((prev) => prev.filter((g) => g.products.length > 0));
      return;
    }

    if (destination.droppableId === "newTableDroppable") {
      const uniqueName = generateUniqueGroupName("Grupo");
      setEquipoSearchTerm("");
      if (source.droppableId === "unassignedDroppable") {
        const newProducts = [...addedProducts];
        const [movedItem] = newProducts.splice(source.index, 1);
        setAddedProducts(newProducts);
        const newGroup = { groupName: uniqueName, products: [movedItem] };
        setAddedGroups((prev) => [...prev, newGroup]);
      } else if (source.droppableId.startsWith("group-")) {
        const sourceGroupName = source.droppableId.replace("group-", "");
        const groupIndex = addedGroups.findIndex(
          (g) => g.groupName === sourceGroupName
        );
        if (groupIndex !== -1) {
          const sourceGroupProducts = [...addedGroups[groupIndex].products];
          const [movedItem] = sourceGroupProducts.splice(source.index, 1);
          const newGroups = [...addedGroups];
          newGroups[groupIndex] = {
            ...newGroups[groupIndex],
            products: sourceGroupProducts,
          };
          setAddedGroups(newGroups);
          const newGroup = { groupName: uniqueName, products: [movedItem] };
          setAddedGroups((prev) => [...prev, newGroup]);
        }
      }
      setAddedGroups((prev) => prev.filter((g) => g.products.length > 0));
    }
  };

  const handleDragStart = (e, item, itemType) => {
    e.currentTarget.style.cursor = "grabbing";
    let itemsToDrag = [];
    if (itemType === "product") {
      if (selectedProductIds.includes(item.id)) {
        itemsToDrag = productos.filter((p) =>
          selectedProductIds.includes(p.id)
        );
      } else {
        itemsToDrag = [item];
      }
    } else {
      itemsToDrag = [item];
    }
    e.dataTransfer.setData(
      "application/json",
      JSON.stringify({ items: itemsToDrag, itemType })
    );
  };

  // Leer clientes con getDocs (además de la suscripción)
  useEffect(() => {
    const fetchClientes = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "clientes"));
        const clientesData = [];
        querySnapshot.forEach((doc) => {
          clientesData.push({ id: doc.id, ...doc.data() });
        });
        const uniqueClientes = clientesData.filter(
          (cliente, index, self) =>
            index === self.findIndex((c) => c.empresa === cliente.empresa)
        );
        setClientesList(uniqueClientes);
      } catch (error) {
        console.error("Error al obtener los clientes:", error);
      }
    };
    fetchClientes();
  }, []);

  const handleGuardar = async () => {
    if (initialData && !editMode) {
      Swal.fire({
        icon: "info",
        title: "Ahora puedes editar el archivo",
        text: "Puedes modificar los campos y luego actualizar el archivo",
        timer: 2000,
        showConfirmButton: false,
      });
      setEditMode(true);
      return;
    }
    if (!nombre.trim()) {
      Swal.fire({
        icon: "warning",
        title: "Campo obligatorio",
        text: "El campo Nombre o Referencia* es obligatorio.",
      });
      return;
    }
    const reducedProducts = addedProducts.map((p) => ({
      id: p.id,
      nombre: p.nombre,
      costo: p.costo,
      quantity: p.quantity,
      subtotal: p.subtotal,
    }));
    const reducedGroups = addedGroups.map((g) => ({
      groupName: g.groupName,
      products: g.products.map((p) => ({
        id: p.id,
        nombre: p.nombre,
        costo: p.costo,
        quantity: p.quantity,
        subtotal: p.subtotal,
      })),
    }));
    let fechaVencLocal = null;
    if (
      typeof fechaVencimiento === "string" &&
      fechaVencimiento.trim() !== ""
    ) {
      fechaVencLocal = new Date(fechaVencimiento + "T00:00:00");
    }
    const data = {
      nombre,
      numero,
      cliente,
      fechaEmision: fechaEmision || "",
      fechaVencimiento: fechaVencLocal
        ? fechaVencLocal.toISOString().split("T")[0]
        : "",
      descripcion,
      moneda,
      impuesto,
      addedProducts: reducedProducts,
      addedGroups: reducedGroups,
      files,
      unassignedTitle,
    };
    const isNew = !initialData;
    try {
      const gastosRef = collection(db, "gastos");
      const qGastos = query(gastosRef, where("numero", "==", numero));
      const snap = await getDocs(qGastos);
      if (!snap.empty) {
        if (isNew || snap.docs.some((docu) => docu.id !== initialData.id)) {
          Swal.fire({
            icon: "warning",
            title: "Número duplicado",
            text: "Ya existe un archivo con este número. Por favor utiliza uno diferente.",
          });
          return;
        }
      }
      if (initialData) {
        await updateDoc(doc(db, "gastos", initialData.id), data);
        Swal.fire({
          title: "¡Actualizado correctamente!",
          text: "Tu archivo se ha actualizado.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          if (onSaveSuccess) onSaveSuccess();
          closeModal();
        });
      } else {
        await addDoc(gastosRef, data);
        if (modoNumero === "auto") {
          const numRef = doc(db, "config", "numeracion");
          await updateDoc(numRef, {
            siguienteNumero: parseInt(siguienteNumero, 10) + 1,
          });
        }
        Swal.fire({
          title: "¡Guardado correctamente!",
          text: "Se ha guardado el archivo.",
          icon: "success",
          confirmButtonText: "OK",
        }).then(() => {
          if (onSaveSuccess) onSaveSuccess();
          closeModal();
        });
      }
    } catch (error) {
      console.error("Error al guardar el documento:", error);
      Swal.fire({
        title: initialData ? "Error al actualizar" : "Error al guardar",
        text: "Ocurrió un problema al guardar tus datos.",
        icon: "error",
        confirmButtonText: "OK",
      });
    }
  };

  const handleChange = (event) => {
    setMoneda(event.target.value);
  };

  const handleAdd = () => {
    const selectedCats = categorias.filter((cat) =>
      selectedProductIds.includes(cat.id)
    );
    const selectedGroups = grupos.filter((g) =>
      selectedProductIds.includes(g.id)
    );
    const selectedProds = productos.filter((p) =>
      selectedProductIds.includes(p.id)
    );
    let tempGroups = [];
    selectedGroups.forEach((grupo) => {
      const groupProducts = productos.filter((p) => p.grupo === grupo.nombre);
      const mappedProds = groupProducts.map((p) => {
        const q = quantities[p.id] || 1;
        const cost = parseFloat(p.costo) || 0;
        return {
          id: p.id,
          nombre: p.nombre,
          costo: cost,
          quantity: q,
          subtotal: cost * q,
        };
      });
      tempGroups.push({ groupName: grupo.nombre, products: mappedProds });
    });
    setAddedGroups((prev) => {
      const newArr = [...prev];
      tempGroups.forEach((newGroup) => {
        const idx = newArr.findIndex((g) => g.groupName === newGroup.groupName);
        if (idx >= 0) {
          const existingGroup = newArr[idx];
          newGroup.products.forEach((prod) => {
            if (!existingGroup.products.some((p) => p.id === prod.id)) {
              existingGroup.products.push(prod);
            }
          });
          newArr[idx] = { ...existingGroup };
        } else {
          newArr.push(newGroup);
        }
      });
      return newArr;
    });
    const singlesFromProds = selectedProds.map((p) => {
      const q = quantities[p.id] || 1;
      const cost = parseFloat(p.costo) || 0;
      return {
        id: p.id,
        nombre: p.nombre,
        costo: cost,
        quantity: q,
        subtotal: cost * q,
        grupo: p.grupo,
      };
    });
    const singlesFromCats = selectedCats.flatMap((cat) => {
      const catProducts = productos.filter((p) => p.categoria === cat.nombre);
      return catProducts.map((p) => {
        const q = quantities[p.id] || 1;
        const cost = parseFloat(p.costo) || 0;
        return {
          id: p.id,
          nombre: p.nombre,
          costo: cost,
          quantity: q,
          subtotal: cost * q,
          grupo: p.grupo,
        };
      });
    });
    const combinedSingles = [...singlesFromProds, ...singlesFromCats];
    const productsSinGrupo = combinedSingles.filter(
      (prod) =>
        !prod.grupo || !selectedGroups.some((g) => g.nombre === prod.grupo)
    );
    setAddedProducts((prev) => {
      const combined = [...prev, ...productsSinGrupo];
      const deduped = Array.from(
        combined
          .reduce((map, prod) => {
            if (!map.has(prod.id)) {
              map.set(prod.id, prod);
            }
            return map;
          }, new Map())
          .values()
      );
      return deduped;
    });
  };

  const formatNumber = (num) => {
    if (typeof num !== "number") {
      num = parseFloat(num) || 0;
    }
    return num.toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleModoCambio = (e) => {
    const nuevoModo = e.target.value;
    setModoNumero(nuevoModo);
    if (nuevoModo === "manual") {
      setEditableNumero(true);
      setModalConfigIsOpen(false);
      Swal.fire({
        icon: "info",
        title: "Modo Manual Activado",
        text: "Ahora puedes agregar el número manualmente",
      });
    } else if (nuevoModo === "auto") {
      setEditableNumero(false);
      setModalConfigIsOpen(false);
      Swal.fire({
        icon: "info",
        title: "Modo Automático Activado",
        text: "Ahora los números se generarán automáticamente, dependiendo de su configuración.",
      });
    }
  };

  const guardarConfiguracionNumero = async (e) => {
    e.preventDefault();
    try {
      let newConfig;
      if (modoNumero === "auto") {
        const newNumero = `${prefijo}${String(siguienteNumero).padStart(
          4,
          "0"
        )}`;
        setNumero(newNumero);
        setEditableNumero(false);
        newConfig = {
          prefijo,
          siguienteNumero: parseInt(siguienteNumero, 10),
          modoNumero,
        };
      } else {
        newConfig = { prefijo, siguienteNumero: numero, modoNumero };
      }
      await setDoc(doc(db, "config", "numeracion"), newConfig);
      setConfig(newConfig);
      Swal.fire({
        icon: "success",
        title: "Configuración guardada",
        text: "Se actualizaron tus preferencias de numeración.",
      });
      setModalConfigIsOpen(false);
    } catch (error) {
      console.error("Error guardando configuración:", error);
    }
  };

  const filteredAddedGroups = addedGroups
    .map((grp) => {
      const filteredProducts = grp.products.filter((prod) =>
        prod.nombre.toLowerCase().includes(equipoSearchTerm.toLowerCase())
      );
      return { ...grp, products: filteredProducts };
    })
    .filter((grp) => grp.products.length > 0);

  const filteredAddedProducts = addedProducts.filter((prod) =>
    prod.nombre.toLowerCase().includes(equipoSearchTerm.toLowerCase())
  );

  // === Tasa de IVA robusta a "16%", "16", 16, "16,0", etc. ===
  const ivaRate = (() => {
    const raw = typeof impuesto === "string"
      ? impuesto.replace(",", ".").replace("%", "").trim()
      : impuesto;
    const n = Number(raw);
    return Number.isFinite(n) && n > 0 ? n / 100 : 0; // 0.16 para "16%"
  })();

  return (
    <div className="calculador-form-layout">
      <TopBar
        onSave={handleGuardar}
        initialData={initialData}
        editMode={editMode}
      />
      <div className="content-wrapper">
        <main className="main-section">
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="left-column">
              <GeneralInfo
                nombre={nombre}
                setNombre={setNombre}
                numero={numero}
                editableNumero={editableNumero}
                setNumero={setNumero}
                clientesList={clientesList}
                cliente={cliente}
                setCliente={setCliente}
                fechaEmision={fechaEmision}
                setFechaEmision={setFechaEmision}
                fechaVencimiento={fechaVencimiento}
                setFechaVencimiento={setFechaVencimiento}
                descripcion={descripcion}
                setDescripcion={setDescripcion}
                moneda={moneda}
                handleMonedaChange={handleChange}
                impuesto={impuesto}
                setImpuesto={setImpuesto}
                editMode={editMode}
                openConfigModal={() => setModalConfigIsOpen(true)}
              />
              <div style={{ marginTop: "30px" }}>
                <ProductSelector
                  productos={productos}
                  categorias={categorias}
                  grupos={grupos}
                  productSearchTerm={productSearchTerm}
                  setProductSearchTerm={setProductSearchTerm}
                  activeTab={activeTab}
                  setActiveTab={setActiveTab}
                  isLoading={isLoading}
                  hoveredProductId={hoveredProductId}
                  setHoveredProductId={setHoveredProductId}
                  selectedProductIds={selectedProductIds}
                  setSelectedProductIds={setSelectedProductIds}
                  quantities={quantities}
                  setQuantities={setQuantities}
                  handleDragStart={handleDragStart}
                  handleAdd={handleAdd}
                />
              </div>
            </div>
            <div className="right-column-no-gap">
              <RightPanel
                nombre={nombre}
                equipoSearchTerm={equipoSearchTerm}
                setEquipoSearchTerm={setEquipoSearchTerm}
                filteredAddedGroups={filteredAddedGroups}
                filteredAddedProducts={filteredAddedProducts}
                checkedItems={checkedItems}
                handleCheckItem={handleCheckItem}
                handleCheckAllGroup={handleCheckAllGroup}
                handleDeleteSelectedInGroup={handleDeleteSelectedInGroup}
                formatNumber={formatNumber}
                editMode={editMode}
                handleProductQuantityChange={handleProductQuantityChange}
                handleCostChangeInGroup={handleCostChangeInGroup}
                handleUnassignedProductQuantityChange={
                  handleUnassignedProductQuantityChange
                }
                handleCostChangeInUnassigned={handleCostChangeInUnassigned}
                handleDeleteSelectedInUnassigned={
                  handleDeleteSelectedInUnassigned
                }
                handleDragEnd={handleDragEnd}
                unassignedTitle={unassignedTitle}
                setUnassignedTitle={setUnassignedTitle}
                handleCheckAllUnassigned={handleCheckAllUnassigned}
                handleDrop={handleDrop}
                handleGroupTitleChange={handleGroupTitleChange}
                impuestoRate={ivaRate}
                moneda={moneda} 
              />

              {/* Aquí forzamos 30px debajo del RightPanel */}
              <div style={{ marginTop: "30px" }}>
                <FilesSection
                  files={files}
                  handleUploadClick={handleUploadClick}
                  fileInputRef={fileInputRef}
                  handleFileSelect={handleFileSelect}
                  handleDeleteFile={handleDeleteFile}
                  editMode={editMode}
                />
              </div>
            </div>
          </DragDropContext>
        </main>
      </div>
      <ModalConfig
        modalConfigIsOpen={modalConfigIsOpen}
        setModalConfigIsOpen={setModalConfigIsOpen}
        modoNumero={modoNumero}
        handleModoCambio={handleModoCambio}
        guardarConfiguracionNumero={guardarConfiguracionNumero}
        prefijo={prefijo}
        setPrefijo={setPrefijo}
        siguienteNumero={siguienteNumero}
        setSiguienteNumero={setSiguienteNumero}
      />
    </div>
  );
};

export default CalculadorForm;
